import "../../../styles/calendar.scss";

import Icon, {iconColor, iconTypes} from "./Icon.jsx";

import {Localization} from "../util/localization.jsx";
import PropTypes from "prop-types";
import React from "react";
import moment from "moment";

class CalendarDate extends React.PureComponent {
    constructor(props) {
        super(props);

        this.onClick = this.onClick.bind(this);
    }

    onClick(event) {
        event.stopPropagation();
        event.preventDefault();

        if (this.props.onSelectDate && this.props.isValidDate(this.props.date)) {
            this.props.onSelectDate(this.props.date.format("YYYY-MM-DD"));
        }
    }

    render() {
        const {
            date,
            hasAction,
            isDate,
            isPreviousDate,
            isSelectedMonth,
            isNextOrderDate,
            isToday,
            isValidDate,
            onSelectDate,
            hasOrderLines,
            hasNewSeasonLines,
        } = this.props;
        const dateHasAction = hasAction(date);
        const dateIsDate = isDate(date);
        const dateIsNextOrderDate = isNextOrderDate(date);
        const dateIsPreviousDate = isPreviousDate(date);
        const dateIsSelectedMonth = isSelectedMonth(date);
        const dateIsToday = isToday(date);
        const dateIsValidDate = isValidDate(date);
        const dateHasOrderLines = hasOrderLines(date);
        const dateHasNewSeasonLines = hasNewSeasonLines(date);
        const iconType = dateIsPreviousDate  ? iconTypes.circleChecked  : dateHasOrderLines ? iconTypes.box : iconTypes.circleInner;
        const color = dateIsNextOrderDate ? iconColor.red : dateHasOrderLines && dateIsDate && !dateIsNextOrderDate ? iconColor.white : undefined;

        return (
            <div className={`date${dateIsDate ? " selected" : ""}${dateIsToday ? " today" : ""}${dateIsSelectedMonth ? " in-month" : ""}${dateHasAction ? " " : ""}${(dateIsValidDate && onSelectDate) ? " valid-date" : ""}`} onClick={this.onClick}>
                <div className="number">{date.date()}</div>
                {dateHasAction && (
                    dateHasNewSeasonLines ? (
                        <div>
                            <div>
                                <Icon type={iconTypes.exclamation} className="exclamation"/>
                            </div>
                            <Icon type={iconType} opacity={1}  color={color}/>  
                        </div>       
                    ) : (
                        <Icon type={iconType} opacity={1}  color={color}/>        
                    )                    
                )}
            </div>
        );
    }
}

CalendarDate.propTypes = {
    date: PropTypes.object.isRequired,
    hasAction: PropTypes.func.isRequired,
    isDate: PropTypes.func.isRequired,
    isNextOrderDate: PropTypes.func.isRequired,
    isPreviousDate: PropTypes.func.isRequired,
    isSelectedMonth: PropTypes.func.isRequired,
    isToday: PropTypes.func.isRequired,
    isValidDate: PropTypes.func.isRequired,
    hasOrderLines: PropTypes.func.isRequired,
    hasNewSeasonLines: PropTypes.func.isRequired,
    onSelectDate: PropTypes.func
};

class Calendar extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            date: undefined,
            prevDate: undefined,
            prevValidDates: undefined,
            prevPreviousDates: undefined,
            minDate: undefined,
            maxDate: undefined,
            visibleMonth: undefined,
            nextOrderDate: undefined,
            prevNextOrderDate: undefined
        };

        this.showPreviousMonth = this.showPreviousMonth.bind(this);
        this.showNextMonth = this.showNextMonth.bind(this);
        this.translateMonthName = this.translateMonthName.bind(this);
        this.isDate = this.isDate.bind(this);
        this.isToday = this.isToday.bind(this);
        this.isSelectedMonth = this.isSelectedMonth.bind(this);
        this.hasAction = this.hasAction.bind(this);
        this.isNextOrderDate = this.isNextOrderDate.bind(this);
        this.isPreviousDate = this.isPreviousDate.bind(this);
        this.isValidDate = this.isValidDate.bind(this);
        this.hasOrderLines = this.hasOrderLines.bind(this);
        this.hasNewSeasonLines = this.hasNewSeasonLines.bind(this);
    }

    static getDerivedStateFromProps(props, state) {
        let newState = null;
        let calculateRange = false;

        if (state.prevNextOrderDate !== props.nextOrderDate) {
            newState = newState || {};
            newState  = {
                ...newState,
                prevNextOrderDate: props.nextOrderDate,
                nextOrderDate: moment(props. nextOrderDate).startOf("date")
            };
        }

        if (state.prevDate !== props.date) {
            newState = newState || {};
            newState = {
                ...newState,
                date: moment(props.date).startOf("date"),
                prevDate: props.date
            };
            calculateRange = true;
        }

        if (state.validDates === undefined || state.prevValidDates !== props.validDates) {
            newState = newState || {};
            newState = {
                ...newState,
                prevValidDates: props.validDates,
                validDates: new Set((props.validDates || []).map(d => moment(d).valueOf()))
            };

            calculateRange = true;
        }

        if (state.previousDates === undefined || state.prevPreviousDates !== props.previousDates) {
            newState = newState || {};
            newState = {
                ...newState,
                prevPreviousDates: props.previousDates,
                previousDates: new Set((props.previousDates || []).map(d => moment(d).valueOf()))
            };

            calculateRange = true;
        }

        if (props.minDate && props.maxDate)
        {
            newState = {
                ...newState,
                minDate: props.minDate,
                maxDate: props.maxDate
            };
            return newState; 
        }

        if (calculateRange || !state.minDate || !state.maxDate) {
            if (props.date || ((newState.validDates || state.validDates).size + (newState.previousDates || state.previousDates).size) > 0) {
                let min = moment("2100-01-01");
                let max = moment("1900-01-01");

                if (props.date) {
                    min = (newState.date || state.date).isBefore(min) ? (newState.date || state.date) : min;
                    max = (newState.date || state.date).isAfter(max) ? (newState.date || state.date) : max;
                }

                if (props.nextOrderDate) {
                    min = (newState.nextOrderDate || state.nextOrderDate).isBefore(min) ? (newState.nextOrderDate || state.nextOrderDate) : min;
                    max = (newState.nextOrderDate || state.nextOrderDate).isAfter(max) ? (newState.nextOrderDate || state.nextOrderDate) : max;
                }

                for (const d of (newState.validDates || state.validDates)) {
                    const m = moment(d);
                    min = m.isBefore(min) ? m : min;
                    max = m.isAfter(max) ? m : max;
                }

                for (const d of (newState.previousDates || state.previousDates)) {
                    const m = moment(d);
                    min = m.isBefore(min) ? m : min;
                    max = m.isAfter(max) ? m : max;
                }

                newState = {
                    ...newState,
                    minDate: min,
                    maxDate: max
                };
            } else {
                newState = {
                    ...newState,
                    minDate: moment().startOf("date"),
                    maxDate: moment().startOf("date")
                };
            }
        }

        return newState;
    }

    showPreviousMonth(event) {
        event.stopPropagation();
        event.preventDefault();

        const newDate = (this.state.visibleMonth && this.state.visibleMonth.clone()) || (this.state.date && this.state.date.clone().startOf("month")) || moment().startOf("month");
        newDate.subtract(1, "months");

        if (newDate.isSameOrAfter(this.state.minDate.clone().startOf("month"))) {
            this.setState({
                visibleMonth: newDate
            });
        }
    }

    showNextMonth(event) {
        event.stopPropagation();
        event.preventDefault();

        const newDate = (this.state.visibleMonth && this.state.visibleMonth.clone()) || (this.state.date && this.state.date.clone().startOf("month")) || moment().startOf("month");
        newDate.add(1, "months");

        if (newDate.isSameOrBefore(this.state.maxDate.clone().endOf("month"))) {
            this.setState({
                visibleMonth: newDate
            });
        }
    }

    translateMonthName(number) {
        // Localized later on
        switch (number + 1) {
        case 1:
            return this.props.localization.text("calendar", "january");
        case 2:
            return this.props.localization.text("calendar", "february");
        case 3:
            return this.props.localization.text("calendar", "march");
        case 4:
            return this.props.localization.text("calendar", "april");
        case 5:
            return this.props.localization.text("calendar", "may");
        case 6:
            return this.props.localization.text("calendar", "june");
        case 7:
            return this.props.localization.text("calendar", "july");
        case 8:
            return this.props.localization.text("calendar", "august");
        case 9:
            return this.props.localization.text("calendar", "september");
        case 10:
            return this.props.localization.text("calendar", "october");
        case 11:
            return this.props.localization.text("calendar", "november");
        case 12:
            return this.props.localization.text("calendar", "december");
        }
    }

    isDate(d)  {
        return this.state.date && d.isSame(this.state.date);
    }

    isToday(d) {
        const today = moment().startOf("date");
        return d.isSame(today);
    }

    isSelectedMonth(d) {
        const visibleMonth = this.state.visibleMonth || (this.state.date && this.state.date.clone().startOf("month")) || moment().startOf("month");
        return d.year() === visibleMonth.year() && d.month() === visibleMonth.month();
    }

    hasAction(d) {
        return (this.state.validDates.has(d.valueOf()) || this.state.previousDates.has(d.valueOf()) || (this.state.nextOrderDate && this.state.nextOrderDate.isSame(d)));
    }
    
    isNextOrderDate(d) {
        return this.state.nextOrderDate && this.state.nextOrderDate.isSame(d);
    }

    isPreviousDate(d) {
        return this.state.previousDates.has(d.valueOf());
    }

    isValidDate(d) {
        if(this.props.maxDate && this.props.minDate) {
            return true;
        }
        else {
            return this.state.validDates.has(d.valueOf());
        }       
    }

    hasOrderLines(d) {        
        return this.isValidDate(d) && this.props.deliveryDates.some(dr => moment(dr.deliveryDate).format("YYYY-MM-DD") === d.format("YYYY-MM-DD") && dr.hasOrderLines);
    }

    hasNewSeasonLines(d) {        
        return this.isValidDate(d) && this.props.newSeasonDistrDates.some(dr => moment(dr).format("YYYY-MM-DD") === d.format("YYYY-MM-DD"));
    }

    render() {
        const {
            localization,
        } = this.props;

        const visibleMonth = this.state.visibleMonth || (this.state.date && this.state.date.clone().startOf("month")) || moment().startOf("month");
        
        let d = visibleMonth.clone().isoWeekday(1);
        const weeks = [];

        for (let i = 0; i < 6; i++) {
            const week = [];
            for (let j = 0; j < 7; j++) {
                week.push(d);
                d = d.clone().add(1, "days");
            }
            if (week.reduce((containsSameMonth, d) => containsSameMonth || (d.get("month") === visibleMonth.get("month"), false))) {
                weeks.push(week);
            }
        }

        return (
            <div className="calendar-component">
                <div className="menu-row">
                    <button className="previous-month" onClick={this.showPreviousMonth}>
                        <Icon type={iconTypes.left}/>
                    </button>
                    <div className="month-year">{this.translateMonthName(visibleMonth.month())} {visibleMonth.year()}</div>
                    <button className="next-month" onClick={this.showNextMonth}>
                        <Icon type={iconTypes.right}/>
                    </button>
                </div>
                <div className="week-days">
                    <div className="week-day">{localization.text("calendar", "mondayShort")}</div>
                    <div className="week-day">{localization.text("calendar", "tuesdayShort")}</div>
                    <div className="week-day">{localization.text("calendar", "wednesdayShort")}</div>
                    <div className="week-day">{localization.text("calendar", "thursdayShort")}</div>
                    <div className="week-day">{localization.text("calendar", "fridayShort")}</div>
                    <div className="week-day week-end">{localization.text("calendar", "saturdayShort")}</div>
                    <div className="week-day week-end">{localization.text("calendar", "sundayShort")}</div>
                </div>
                <div className="dates">
                    {weeks.map((week, idx) => (
                        <div className="week" key={idx}>
                            {week.map((date, id) => (
                                <CalendarDate date={date} key={id} isDate={this.isDate} isToday={this.isToday} isSelectedMonth={this.isSelectedMonth} hasAction={this.hasAction} isNextOrderDate={this.isNextOrderDate} isPreviousDate={this.isPreviousDate} isValidDate={this.isValidDate} onSelectDate={this.props.onSelectDate} hasOrderLines={this.hasOrderLines} hasNewSeasonLines={this.hasNewSeasonLines}/>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

Calendar.propTypes = {
    // The set date - for now, expected in the form YYYY-MM-DD
    date: PropTypes.string,
    // The next order date - for now, expected in the form YYYY-MM-DD
    nextOrderDate: PropTypes.string,
    // Callback function for when a date is selected. Gets a string in the form YYYY-MM-DD as a parameter.
    onSelectDate: PropTypes.func,
    // Previous dates in the calendar which are marked with a check box (also list them in validDates to make them clickable) - array of YYYY-MM-DD strings
    previousDates: PropTypes.arrayOf(PropTypes.string.isRequired),
    // The clickable dates - array of YYYY-MM-DD strings
    validDates: PropTypes.arrayOf(PropTypes.string.isRequired),
    // Sames as validDates but with info about existing order lines
    deliveryDates: PropTypes.array.isRequired,
    newSeasonDistrDates :  PropTypes.array.isRequired,

    localization: Localization.propTypes.isRequired,
    minDate: PropTypes.object,
    maxDate: PropTypes.object,
};

Calendar.defaultProps = {
    dropDown: true,
};

export default Calendar;